import { Device, DeviceId, DeviceWarning, Group, License, PolicyStatus } from "@dashboard/devices/types";
import { Provisioning } from "@dashboard/provisioning/types/index";
import { getDeviceBatteryIcon } from "@dashboard/shared/battery";
import { createEntityAdapter } from "@reduxjs/toolkit";

import { getDevicePolicyStatus } from "../../utils/license";

export const provisioningsAdapter = createEntityAdapter<Provisioning>({
    sortComparer: (a, b) => a?.name?.localeCompare(b?.name),
});

export const groupsAdapter = createEntityAdapter<Group>({
    sortComparer: (a, b) => a?.name?.localeCompare(b?.name),
});

export const licensesAdapter = createEntityAdapter<License>({});

export const devicesAdapter = createEntityAdapter<Device>({});

type LicensesNormalized = Record<number, License>;

type DevicesNormalized = Record<DeviceId, Device>;

type ProvisioningsNormalized = Record<number, Provisioning>;

type NormalizedEntities = {
    licenses: LicensesNormalized;
    devices: DevicesNormalized;
    provisionings: ProvisioningsNormalized;
};

export function getDeviceWarnings(device: API.Device, groups: Group[]): DeviceWarning[] {
    const warnings: DeviceWarning[] = [];

    // ignore unused licenses
    if (!device) {
        return warnings;
    }

    if (device?.lastState?.memory?.isLow) {
        warnings.push("lowMemory");
    }

    const battery = device?.lastState?.battery;
    const batteryIcon = getDeviceBatteryIcon(battery);
    if (batteryIcon.critical) {
        warnings.push("lowBattery");
    }

    const policyStatus = getDevicePolicyStatus(device, groups);
    if (policyStatus === PolicyStatus.NON_COMPLIANT) {
        warnings.push("nonCompliant");
    }

    return warnings;
}

export function normalizeDevice(device: API.Device, groups: Group[]): Device {
    return {
        ...device,
        warnings: getDeviceWarnings(device, groups),
    };
}

export const normalizeLicenses = (licenses: API.License[], groups: Group[]): Pick<NormalizedEntities, "licenses" | "devices"> => {
    const newLicenses: LicensesNormalized = {};
    const newDevices: DevicesNormalized = {};

    licenses.forEach((license) => {
        if (license.device) {
            const newDevice: Device = normalizeDevice(license.device, groups);
            newLicenses[license.id] = { ...license, device: newDevice, queryMap: {} };
            newDevices[license.device.id] = newDevice;
        } else {
            newLicenses[license.id] = { ...license, device: null, queryMap: {} };
        }
    });

    return {
        licenses: newLicenses,
        devices: newDevices,
    };
};

export const normalizeProvisionings = (provisionings: API.Provisioning[]): ProvisioningsNormalized => {
    const normalizedProvisionings: ProvisioningsNormalized = {};

    provisionings.forEach((provisioning) => {
        normalizedProvisionings[provisioning.id] = provisioning;
    });

    return normalizedProvisionings;
};
